import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authApi, getToken, setToken, resetSessionExpiredFlag } from '@/api/apiClient';

const AuthContext = createContext();

function normalizePermissoes(permissoes) {
  if (permissoes == null) return undefined;
  let p = permissoes;
  if (typeof p === 'string') {
    try {
      p = JSON.parse(p);
    } catch {
      return undefined;
    }
  }
  if (typeof p !== 'object' || p === null) return undefined;
  return {
    ...p,
    modulos: Array.isArray(p.modulos) ? p.modulos : [],
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings] = useState(null);
  const [empresasVinculadas, setEmpresasVinculadas] = useState([]);

  const checkUserAuth = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setEmpresa(null);
      setEmpresasVinculadas([]);
      setIsAuthenticated(false);
      setIsLoadingAuth(false);
      return { ok: false, reason: 'no_token' };
    }
    try {
      setIsLoadingAuth(true);
      setAuthError(null);
      const data = await authApi.me();
      const u = data.usuario || data;
      const e = data.empresa;
      const permissoes = normalizePermissoes(u.permissoes);
      setUser({
        ...u,
        permissoes,
        company_id: u.empresaId || e?.id,
        role: u.perfil,
        data: { company_id: u.empresaId || e?.id },
      });
      setEmpresa(e ? {
        id: e.id,
        nome: e.nomeFantasia || e.razaoSocial,
        razaoSocial: e.razaoSocial,
        nomeFantasia: e.nomeFantasia,
        status: e.status,
        dataExpiracao: e.dataExpiracao,
        plano: e.plano || null,
      } : null);
      setIsAuthenticated(true);
      try {
        const v = await authApi.empresasVinculadas();
        setEmpresasVinculadas(Array.isArray(v?.empresas) ? v.empresas : []);
      } catch {
        setEmpresasVinculadas([]);
      }
      return { ok: true };
    } catch (err) {
      setEmpresasVinculadas([]);
      if (err?.status === 401 || err?.status === 403) {
        setToken(null);
        setUser(null);
        setEmpresa(null);
        setIsAuthenticated(false);
        const message = err?.data?.error || err?.message || 'Sessão expirada';
        setAuthError({ type: 'auth_required', message, code: err?.data?.code });
        return {
          ok: false,
          reason: 'http',
          status: err.status,
          message,
          data: err?.data,
        };
      }
      const message = err?.message || 'Erro ao carregar usuário';
      setAuthError({ type: 'unknown', message });
      return { ok: false, reason: 'unknown', message };
    } finally {
      setIsLoadingAuth(false);
    }
  }, []);

  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  // Sessão expirada em qualquer requisição (401 TOKEN_EXPIRED/INVALID): limpa estado e
  // redireciona ao login uma única vez, evitando páginas presas repetindo 401.
  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setEmpresa(null);
      setEmpresasVinculadas([]);
      setIsAuthenticated(false);
      setAuthError({ type: 'auth_required', message: 'Sua sessão expirou. Faça login novamente.' });
      const path = (window.location.pathname || '').toLowerCase();
      if (!path.includes('/login')) {
        window.location.href = '/Login';
      }
    };
    window.addEventListener('auction:session-expired', onSessionExpired);
    return () => window.removeEventListener('auction:session-expired', onSessionExpired);
  }, []);

  const login = async (email, senha) => {
    setAuthError(null);
    try {
      const data = await authApi.login(email, senha);
      setToken(data.token);
      resetSessionExpiredFlag();
      // Usar os mesmos dados do /auth/me para manter menu e permissões consistentes
      const authResult = await checkUserAuth();
      if (!authResult.ok) {
        const msg =
          authResult.message ||
          (authResult.reason === 'no_token'
            ? 'Não foi possível validar o acesso após o login.'
            : 'Não foi possível entrar.');
        const code = authResult.data?.code;
        setAuthError({
          type: 'login_failed',
          message: msg,
          ...(code ? { code } : {}),
        });
        throw Object.assign(new Error(msg), {
          status: authResult.status ?? (authResult.reason === 'unknown' ? 0 : 401),
          data: authResult.data,
          isUnauthorized: authResult.status === 401,
        });
      }
      return data;
    } catch (err) {
      let message = err?.data?.error || err?.message || 'Login falhou';
      const code = err?.data?.code;
      const hint = err?.data?.hint;
      if (code === 'DATABASE') message += ' (Servidor: falha ao conectar no banco. Verifique DATABASE_URL no Coolify.)';
      else if (code === 'JWT') message += ' (Servidor: JWT não configurado. Defina JWT_SECRET no Coolify.)';
      else if (hint) message += ` [${hint}]`;
      if (code && code !== 'EMPRESA_PENDING_APPROVAL') message += ` — code: ${code}`;
      if (code) setAuthError({ type: 'login_failed', message, code });
      else setAuthError({ type: 'login_failed', message });
      throw err;
    }
  };

  const logout = (shouldRedirect = true) => {
    setToken(null);
    setUser(null);
    setEmpresa(null);
    setEmpresasVinculadas([]);
    setIsAuthenticated(false);
    setAuthError(null);
    if (shouldRedirect) {
      window.location.href = '/Login';
    }
  };

  const switchEmpresa = useCallback(async (empresaId) => {
    const data = await authApi.trocarEmpresa(empresaId);
    if (!data?.token) {
      throw Object.assign(new Error('Resposta inválida do servidor'), { status: 500, data: {} });
    }
    setToken(data.token);
    await checkUserAuth();
    window.location.assign('/Dashboard');
  }, [checkUserAuth]);

  const navigateToLogin = () => {
    window.location.href = '/Login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        empresa,
        empresasVinculadas,
        isAuthenticated,
        isLoadingAuth,
        isLoadingPublicSettings,
        authError,
        appPublicSettings,
        login,
        logout,
        switchEmpresa,
        navigateToLogin,
        checkAppState: checkUserAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
